import {
  daysInMonth,
  isBirthdayOn,
  type Birthday,
  type CalendarBirthday,
  type CalendarMonthEvent,
  type LocalDate,
} from '@veriverse/shared';

/** Client-side helpers for the birthday calendar (spec §6.13). */

export interface YearMonth {
  readonly year: number;
  readonly month: number;
}

export function shiftMonth({ year, month }: YearMonth, delta: number): YearMonth {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/**
 * The month as weeks of seven cells, Monday first (Swedish and British convention). Cells
 * outside the month are `null`, so every row has exactly seven entries.
 */
export function monthGrid({ year, month }: YearMonth): (number | null)[][] {
  // getUTCDay: 0 = Sunday; shift so Monday = 0.
  const offset = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  const cells: (number | null)[] = Array.from({ length: offset }, () => null);
  for (let day = 1; day <= daysInMonth(year, month); day++) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** Localised weekday names, Monday first ("Mon", "mån"). */
export function weekdayNames(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale === 'sv' ? 'sv-SE' : 'en-GB', {
    weekday: 'short',
    timeZone: 'UTC',
  });
  // 5 Jan 2026 was a Monday.
  return Array.from({ length: 7 }, (_, i) => format.format(new Date(Date.UTC(2026, 0, 5 + i))));
}

/** "October 2026" / "Oktober 2026". */
export function monthTitle({ year, month }: YearMonth, locale: string): string {
  const text = new Intl.DateTimeFormat(locale === 'sv' ? 'sv-SE' : 'en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

export interface Me {
  readonly id: string;
  readonly displayName: string;
  readonly birthday: Birthday | null;
}

/**
 * Who to congratulate today: colleagues with a visible birthday, plus the signed-in user even
 * when theirs is hidden (they always know their own birthday). The user comes first.
 */
export function celebrantsToday(
  me: Me,
  monthBirthdays: readonly CalendarBirthday[],
  today: LocalDate,
): string[] {
  const names = monthBirthdays
    .filter((b) => b.day === today.day && b.userId !== me.id)
    .map((b) => b.displayName);
  if (me.birthday && isBirthdayOn(me.birthday, today)) names.unshift(me.displayName);
  return names;
}

/**
 * User ids of everyone celebrated today, by the same rule as `celebrantsToday`: colleagues with a
 * visible birthday, plus the signed-in user even when theirs is hidden (only they see that).
 */
export function celebrantIdsToday(
  me: Me,
  monthBirthdays: readonly CalendarBirthday[],
  today: LocalDate,
): Set<string> {
  const ids = new Set(monthBirthdays.filter((b) => b.day === today.day).map((b) => b.userId));
  ids.delete(me.id);
  if (me.birthday && isBirthdayOn(me.birthday, today)) ids.add(me.id);
  return ids;
}

/** "Ann", "Ann and Bob", "Ann, Bob and Cy" (or "och" in Swedish). */
export function joinNames(names: readonly string[], locale: string): string {
  return new Intl.ListFormat(locale === 'sv' ? 'sv-SE' : 'en-GB', {
    style: 'long',
    type: 'conjunction',
  }).format(names);
}

/** Form value of the day selector: the days a birthday month can have (29 for February). */
export function birthdayDayOptions(month: number): number[] {
  // 2028 is a leap year, so February offers the 29th.
  return Array.from({ length: daysInMonth(2028, month) }, (_, i) => i + 1);
}

/** What happens on one day of the month view: birthday names and company events. */
export interface CalendarDay {
  readonly birthdays: string[];
  readonly events: CalendarMonthEvent[];
}

/** Groups a month's birthdays and company events by day of the month. */
export function entriesByDay(
  birthdays: readonly CalendarBirthday[],
  events: readonly CalendarMonthEvent[],
): Map<number, CalendarDay> {
  const days = new Map<number, CalendarDay>();
  const at = (day: number): CalendarDay => {
    let entry = days.get(day);
    if (!entry) {
      entry = { birthdays: [], events: [] };
      days.set(day, entry);
    }
    return entry;
  };
  for (const b of birthdays) at(b.day).birthdays.push(b.displayName);
  for (const e of events) at(e.day).events.push(e);
  return days;
}
